import { useEffect, useState } from 'react';
import { api } from '../api';
import { avg } from '../format';
import type { StatisticsRow } from '../types';

/** The statistics tab, with the same columns as the source site. */
export function StatisticsPanel({ leagueId }: { leagueId: number }) {
  const [rows, setRows] = useState<StatisticsRow[]>([]);
  const [kind, setKind] = useState<'SINGLE' | 'DOUBLE' | 'ALL'>('SINGLE');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .statistics(leagueId, kind === 'ALL' ? undefined : kind)
      .then((data) => !cancelled && setRows(data))
      .catch((err: unknown) => !cancelled && setError(String(err)));
    return () => {
      cancelled = true;
    };
  }, [leagueId, kind]);

  return (
    <section className="card">
      <header>
        <h2>Statistics</h2>
        <div className="tabs">
          {(['SINGLE', 'DOUBLE', 'ALL'] as const).map((k) => (
            <button key={k} aria-selected={kind === k} onClick={() => setKind(k)}>
              {k === 'SINGLE' ? 'Singles' : k === 'DOUBLE' ? 'Doubles' : 'All'}
            </button>
          ))}
        </div>
      </header>

      {error && <div className="notice error">{error}</div>}

      <p className="hint" style={{ marginBottom: 10 }}>
        Place is derived from the 3-dart average. Averages are recomputed from the stored dart and
        score counters, so they stay exact at any granularity.
      </p>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th className="left">#</th>
              <th className="left">Player</th>
              <th>Average</th>
              <th>Matches</th>
              <th>Won</th>
              <th>180</th>
              <th>140+</th>
              <th>100+</th>
              <th>80+</th>
              <th>9 darts</th>
              <th>12 darts</th>
              <th>15 darts</th>
              <th>18 darts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.participantId}>
                <td className="left muted">{row.place}</td>
                <td className="left">{row.player}</td>
                <td className="num">
                  <strong>{avg(row.average)}</strong>
                </td>
                <td className="num">{row.matches}</td>
                <td className="num">{row.won}</td>
                <td className="num">{row.count180}</td>
                <td className="num">{row.count140}</td>
                <td className="num">{row.count100}</td>
                <td className="num">{row.count80}</td>
                <td className="num muted">{avg(row.average9)}</td>
                <td className="num muted">{avg(row.average12)}</td>
                <td className="num muted">{avg(row.average15)}</td>
                <td className="num muted">{avg(row.average18)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="empty-state">Nothing imported for this league yet.</div>}
    </section>
  );
}
